import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { urlPreviewQuery } from 'networking/mindfeed/queries/urlPreview';
import { hostOfCompleteUrl } from 'utils/UrlUtils';

// A change of this many characters in one step is a paste. Typing inserts
// one character at a time, so this reliably tells the two apart.
const PASTE_CHARACTER_THRESHOLD = 5;

// How long the field must stay still after typing before we ask for a
// preview. Each keystroke restarts the wait, so only a real pause spends a
// request.
const PREVIEW_DEBOUNCE_MS = 600;

// The address that looks finished, or undefined while it's still being
// typed. Same rule as the Mac composer.
const completeUrl = (value: string): string | undefined => {
  const trimmed = value.trim();
  return hostOfCompleteUrl(trimmed) ? trimmed : undefined;
};

// Decides when the link field's address is worth asking cortex about, the
// way the Mac composer does: a pasted address is requested at once, a typed
// one after a pause, and a half-typed one never. Returns the address to
// request, or undefined.
export function useLinkPreviewUrl(value: string): string | undefined {
  const queryClient = useQueryClient();
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(() =>
    completeUrl(value),
  );
  const previousLength = useRef(value.length);

  useEffect(() => {
    const inserted = value.length - previousLength.current;
    previousLength.current = value.length;

    const url = completeUrl(value);
    if (!url) {
      setPreviewUrl(undefined);
      return;
    }

    // Already known (from this session or the feed): no need to wait.
    const isCached = Boolean(
      queryClient.getQueryData(urlPreviewQuery(url).queryKey),
    );
    if (isCached || inserted >= PASTE_CHARACTER_THRESHOLD) {
      setPreviewUrl(url);
      return;
    }

    const timer = setTimeout(() => setPreviewUrl(url), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value, queryClient]);

  return previewUrl;
}
